import { isMrnPrimary } from "@/configuration/mrn-primary";
import { isEcwGroup } from "@/src/lib/ehr/ecw/ecw-constants";
import { enforceShape } from "@/src/lib/enforce-shape";
import { logAndThrow, logger } from "@/src/lib/logging/logger";
import { withDurationMetric } from "@/src/lib/metrics";
import { classifyEhrId } from "@/src/lib/resolve-ehr-ids";
import { withRetry } from "@/src/lib/retry";
import { customCamelCase, customSnakeCase } from "@/src/lib/string";
import { getSupabaseClient } from "@/src/lib/supabase";
import { CoverageRow, PatientRow } from "@/src/lib/types/tables.types";
import {
  formatPhoneNumberAsTyped,
  isPartialPhoneQuery,
  normalizePhoneToNanpDigits,
} from "@/src/lib/utils/date-and-phone-utils";
import { Group } from "@/src/models/group";
import { Patient, PatientStatus, PatientType, patientShape } from "@/src/models/patient";
import { PaginatedPatientFiltersParams } from "@/src/models/patient-filters";
import { PatientMetadata } from "@/src/models/patient-metadata";
import { PersonEthnicity, PersonGender, PersonLanguage, PersonRace } from "@/src/models/person";
import { createOrUpdateAddress, fullAddressSelect, mapAddressRowToAddress } from "@/src/repo/address";
import { BaseRepository } from "@/src/repo/base-repository";
import { fullCoverageSelect, mapCoverageRowToCoverage } from "@/src/repo/coverage";
import { applySearchTextTokenFilter } from "@/src/repo/search-text-query";

export const PATIENT_TABLE_NAME = "patient";
export const fullPatientSelect = `
  *,
  home_address:address!home_address_id(${fullAddressSelect}),
  work_address:address!work_address_id(${fullAddressSelect}),
  coverage:coverage!patient_id(${fullCoverageSelect})
`;

export const dedupePatientSelectWithCanonical = `
  ${fullPatientSelect},
  patient_dedupe!patient_id(
    canonical_patient_id,
    patient_id,
    canonical_patient:patient!canonical_patient_id( ${fullPatientSelect} )
  )
`;

export const dedupePatientSelect = `
  ${fullPatientSelect},
  patient_dedupe!patient_id(
    canonical_patient_id,
    patient_id
  )
`;

export function mapPatientRowToPatient(patientRow: any): Patient {
  const { home_address, work_address, coverage, ...rest } = patientRow;

  const mappedFields: Record<string, any> = {};

  for (const [key, value] of Object.entries(rest)) {
    mappedFields[customCamelCase(key)] = value;

    //TODO: this is a hack to handle the fact that the ethnicity, language, and race are not always provided
    if (key === "ethnicity") {
      mappedFields.ethnicity = value ? value : PersonEthnicity.NOT_PROVIDED;
    }

    if (key === "language") {
      mappedFields.language = value ? value : PersonLanguage.NOT_PROVIDED;
    }

    if (key === "race") {
      mappedFields.race = value ? value : PersonRace.NOT_PROVIDED;
    }
  }

  return {
    ...mappedFields,
    homeAddress: home_address ? mapAddressRowToAddress(home_address) : null,
    workAddress: work_address ? mapAddressRowToAddress(work_address) : null,
    coverages: coverage
      ? coverage.filter(({ deleted_at }: CoverageRow) => deleted_at === null).map(mapCoverageRowToCoverage)
      : null,
  } as Patient;
}

function mapPatientToPatientRow(patient: Patient): PatientRow {
  const { homeAddress, workAddress, coverages, ...rest } = patient;

  const cleanedPatient = enforceShape(rest, patientShape);
  const mappedFields: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(cleanedPatient)) {
    mappedFields[customSnakeCase(key)] = value;
  }

  // Special case for gender: if it's null, set it to UNKNOWN
  if (mappedFields.gender === null) {
    mappedFields.gender = PersonGender.UNKNOWN;
  }

  //TODO: dsy we need to handle the coverage here
  // likely we just ignore becuase its not the proper time to update or change coverage?
  if (coverages) {
    logger.info("mapPatientToPatientRow: ignoring coverages");
  }

  return {
    ...mappedFields,
    home_address_id: homeAddress?.id ?? null,
    work_address_id: workAddress?.id ?? null,
  } as PatientRow;
}

export class PatientRepository extends BaseRepository<Patient, PatientRow> {
  constructor() {
    super({
      tableName: PATIENT_TABLE_NAME,
      selectClause: fullPatientSelect,
      mapRowToModel: mapPatientRowToPatient,
      mapModelToRow: mapPatientToPatientRow,
    });
  }

  /**
   * Detection-only instrumentation for the eCW "double FHIR id" corruption: a write that leaves
   * `ehr_id` === `secondary_ehr_id` with both classified as an EHR id (non-MRN) — i.e. the FHIR
   * resource id ended up in BOTH columns and the real MRN was lost. Scoped to eCW groups. Emits a
   * warning with the caller stack so we can identify the offending write path. Never throws and
   * never alters the write. TODO: remove once the corrupting path is identified.
   */
  override async update(id: string, partial: Partial<Patient>, group: Group): Promise<Patient> {
    const result = await super.update(id, partial, group);

    const touchedIdColumn = "ehrId" in partial || "secondaryEhrId" in partial;
    const ehrId = result.ehrId?.trim() || null;
    const secondaryEhrId = result.secondaryEhrId?.trim() || null;

    if (
      touchedIdColumn &&
      isEcwGroup(group) &&
      ehrId !== null &&
      ehrId === secondaryEhrId &&
      classifyEhrId(ehrId) === "ehrId"
    ) {
      logger.warn("DOUBLE_FHIR_ID_WRITE: eCW patient ehr_id === secondary_ehr_id (FHIR id in both columns, no MRN)", {
        patientId: id,
        groupId: group.id,
        subdomain: group.subdomain,
        ehrId,
        secondaryEhrId,
        updatedKeys: Object.keys(partial),
        // What THIS write set (vs the resulting row) — distinguishes the corrupting write from a
        // later re-touch of an already-corrupted row.
        writtenEhrId: partial.ehrId,
        writtenSecondaryEhrId: partial.secondaryEhrId,
        callerStack: new Error("double-fhir-id-write").stack,
      });
    }

    return result;
  }
}

export const patientRepository = new PatientRepository();
export const getPatient = patientRepository.get.bind(patientRepository);
export const getAllPatientsDB = patientRepository.getAll.bind(patientRepository);
export const insertPatient = patientRepository.insert.bind(patientRepository);
export const updatePatient = patientRepository.update.bind(patientRepository);

export const getPatientGroupId = withDurationMetric(async function getPatientGroupId(
  patientId: string
): Promise<string | null> {
  const { data, error } = await withRetry("getPatientGroupId", async () => {
    const supabaseClient = await getSupabaseClient();

    return supabaseClient.from(PATIENT_TABLE_NAME).select("group_id").eq("id", patientId).is("deleted_at", null);
  });

  if (error) {
    logAndThrow("Error getting patient group", { patientId, error }, true);
  }

  if (!data || data.length !== 1) return null;

  return data[0].group_id;
}, "getPatientGroupId");

export type PatientLookupParams = {
  givenName?: string;
  familyName?: string;
  dateOfBirth?: string;
  type?: PatientType;
  limit?: number;
};

/**
 * Structured, high-precision lookup on the patient table.
 * Used for system-level identification and matching.
 */
export const findPatients = withDurationMetric(async function findPatients(
  params: PatientLookupParams,
  group: Group
): Promise<Patient[]> {
  const { givenName, familyName, dateOfBirth, type, limit = 20 } = params;

  if (!givenName && !familyName && !dateOfBirth && !type) {
    return [];
  }

  const { data, error } = await withRetry("findPatients", async () => {
    const supabaseClient = await getSupabaseClient();

    let query = supabaseClient
      .from(PATIENT_TABLE_NAME)
      .select(fullPatientSelect)
      .eq("group_id", group.id)
      .is("deleted_at", null)
      .not("is_duplicate", "is", true)
      .order("created_at", { ascending: false })
      .limit(limit);

    if (givenName?.trim()) {
      query = query.ilike("given_name", givenName.trim());
    }

    if (familyName?.trim()) {
      query = query.ilike("family_name", familyName.trim());
    }

    if (dateOfBirth) {
      query = query.eq("date_of_birth", dateOfBirth);
    }

    if (type) {
      query = query.eq("type", type.toString());
    }

    return query;
  });

  if (error) {
    throw logAndThrow("Error finding patients", { error }, true);
  }

  return data?.map(mapPatientRowToPatient) ?? [];
}, "findPatients");

export type PatientSearchParams = {
  query: string;
  type?: PatientType;
  limit?: number;
};

/**
 * Fuzzy, free-text search on the patients_metadata view.
 * Used for UI-level discovery and global search bars.
 */
export const searchPatients = withDurationMetric(async function searchPatients(
  params: PatientSearchParams,
  group: Group
): Promise<Patient[]> {
  const { query, type, limit = 20 } = params;
  const trimmed = query.trim();

  if (!trimmed) {
    return [];
  }

  const supabaseClient = await getSupabaseClient();

  let metaQuery = applySearchTextTokenFilter(
    supabaseClient.from(PATIENTS_METADATA_VIEW_NAME).select("id").eq("group_id", group.id),
    trimmed
  )
    .order("created_at", { ascending: false })
    .limit(limit);

  if (type) {
    metaQuery = metaQuery.eq("type", type.toString());
  }

  const { data: metaRows, error: metaError } = await metaQuery;

  if (metaError) {
    throw logAndThrow("Error searching patients metadata", { error: metaError }, true);
  }

  const ids = (metaRows ?? []).map((row) => row.id).filter((id): id is string => !!id);

  return ids.length > 0 ? getPatientsByIds(ids, group) : [];
}, "searchPatients");

export const handlePatientRelations = withDurationMetric(async function handlePatientRelations(
  patient: Patient,
  group: Group
): Promise<Patient> {
  const [homeAddress, workAddress] = await Promise.all([
    patient.homeAddress ? createOrUpdateAddress(patient.homeAddress, group) : Promise.resolve(null),
    patient.workAddress ? createOrUpdateAddress(patient.workAddress, group) : Promise.resolve(null),
  ]);

  return {
    ...patient,
    homeAddress,
    workAddress,
  };
}, "handlePatientRelations");

export const getPatientByDisplayId = withDurationMetric(async function getPatientByDisplayId(
  displayId: string,
  group: Group
): Promise<Patient | null> {
  const { data, error } = await withRetry("getPatientByDisplayId", async () => {
    const supabaseClient = await getSupabaseClient();

    return supabaseClient
      .from(PATIENT_TABLE_NAME)
      .select(fullPatientSelect)
      .eq("group_id", group.id)
      .is("deleted_at", null)
      .eq("display_id", displayId);
  });

  if (error) {
    logger.error("Error fetching patient by display ID:", {
      error,
    });
    throw new Error("Error fetching patient");
  }

  if (!data || data.length === 0) {
    logger.info(`No patient found with display ID ${displayId} for group ${group.id}`, {
      displayId,
      groupId: group.id,
    });

    return null;
  }

  if (data.length > 1) {
    logger.error("Multiple patients found with the same display ID:", {
      data,
    });

    return null;
  }

  return mapPatientRowToPatient(data[0]);
}, "getPatientByDisplayId");

export const getPatientsByIds = withDurationMetric(async function getPatientsByIds(
  ids: string[],
  group: Group
): Promise<Patient[]> {
  if (!ids || ids.length === 0) {
    return [];
  }

  const uniqueIds = [...new Set(ids)];

  const { data, error } = await withRetry("getPatientsByIds", async () => {
    const supabaseClient = await getSupabaseClient();

    return supabaseClient.rpc("get_patients_by_ids", {
      patient_ids: uniqueIds,
      input_group_id: group.id,
    });
  });

  if (error) {
    logger.error("Error fetching patients by IDs:", {
      totalIds: uniqueIds.length,
      error,
    });
    throw new Error("Error fetching patients");
  }

  if (!data) {
    return [];
  }

  let patientsArray: unknown[];

  if (Array.isArray(data)) {
    patientsArray = data;
  } else if (typeof data === "string") {
    try {
      const parsed = JSON.parse(data);

      patientsArray = Array.isArray(parsed) ? parsed : [];
    } catch {
      logger.error("Error parsing RPC JSONB response", {
        data,
      });

      return [];
    }
  } else {
    logger.error("Unexpected RPC response format", {
      dataType: typeof data,
      data,
    });

    return [];
  }

  if (patientsArray.length === 0) {
    return [];
  }

  return patientsArray.map((row: unknown) => mapPatientRowToPatient(row as Record<string, unknown>));
}, "getPatientsByIds");

export const updateStatus = withDurationMetric(async function updateStatus(
  patientId: string,
  newStatus: PatientStatus,
  group: Group
) {
  logger.info(`Updating status for patient ${patientId} to ${newStatus}`, {
    patientId,
    newStatus,
  });

  return updatePatient(patientId, { status: newStatus }, group);
}, "updateStatus");

export const getPatientByEhrId = withDurationMetric(async function getPatientByEhrId(
  ehrId: string,
  groupId: string
): Promise<Patient | null> {
  const { data, error } = await withRetry("getPatientByEhrId", async () => {
    const supabaseClient = await getSupabaseClient();

    return supabaseClient
      .from(PATIENT_TABLE_NAME)
      .select(fullPatientSelect)
      .eq("ehr_id", ehrId)
      .eq("group_id", groupId)
      .is("deleted_at", null)
      .not("is_duplicate", "is", true);
  });

  if (error) {
    logger.error(`Error fetching patient by EHR ID`, {
      ehrId,
      error,
    });
    throw new Error("Error fetching patient by EHR ID");
  }

  if (!data || data.length === 0) {
    return null;
  }

  if (data.length > 1) {
    logger.warn("Multiple patients found with the same EHR ID", {
      data,
    });
  }

  return mapPatientRowToPatient(data[0]);
}, "getPatientByEhrId");

export const getPatientBySecondaryEhrId = withDurationMetric(async function getPatientBySecondaryEhrId(
  secondaryEhrId: string,
  groupId: string
): Promise<Patient | null> {
  const { data, error } = await withRetry("getPatientBySecondaryEhrId", async () => {
    const supabaseClient = await getSupabaseClient();

    return supabaseClient
      .from(PATIENT_TABLE_NAME)
      .select(fullPatientSelect)
      .eq("secondary_ehr_id", secondaryEhrId)
      .eq("group_id", groupId)
      .is("deleted_at", null)
      .not("is_duplicate", "is", true);
  });

  if (error) {
    logger.error("Error fetching patient by secondary EHR ID", {
      secondaryEhrId,
      error,
    });
    throw new Error("Error fetching patient by secondary EHR ID");
  }

  if (!data || data.length === 0) {
    return null;
  }

  if (data.length > 1) {
    logger.warn("Multiple patients found with the same secondary EHR ID", {
      secondaryEhrId,
      data,
    });
  }

  return mapPatientRowToPatient(data[0]);
}, "getPatientBySecondaryEhrId");

/** Includes duplicate-marked rows so referrals remain ordered across duplicate patient records. */
export const getPatientIdsByEhrSystemId = withDurationMetric(async function getPatientIdsByEhrSystemId(
  ehrSystemId: string,
  group: Group
): Promise<string[]> {
  const { data, error } = await withRetry("getPatientIdsByEhrSystemId", async () => {
    const supabaseClient = await getSupabaseClient();
    const ehrSystemIdColumn = isMrnPrimary(group) ? "secondary_ehr_id" : "ehr_id";

    return supabaseClient
      .from(PATIENT_TABLE_NAME)
      .select("id")
      .eq("group_id", group.id)
      .eq(ehrSystemIdColumn, ehrSystemId)
      .is("deleted_at", null);
  });

  if (error) {
    throw logAndThrow("Error fetching patient IDs by EHR system ID", {
      groupId: group.id,
      error,
    });
  }

  return [...new Set((data ?? []).map((patient) => patient.id))];
}, "getPatientIdsByEhrSystemId");

type GetPatientsByPhoneNumberParams = {
  groupId: string;
  phoneNumber: string;
};

/**
 * Returns ALL patients matching either mobilePhoneNumber or homePhoneNumber.
 */
export const getPatientsByPhoneNumber = withDurationMetric(async function getPatientsByPhoneNumber(
  params: GetPatientsByPhoneNumberParams
): Promise<Patient[]> {
  const { groupId, phoneNumber } = params;
  const { data, error } = await withRetry("getPatientsByPhoneNumber", async () => {
    const supabaseClient = await getSupabaseClient();

    return supabaseClient
      .from(PATIENT_TABLE_NAME)
      .select(fullPatientSelect)
      .eq("group_id", groupId)
      .is("deleted_at", null)
      .not("is_duplicate", "is", true)
      .or(`mobile_phone_number.eq.${phoneNumber},home_phone_number.eq.${phoneNumber}`);
  });

  if (error) {
    logger.error(`Error fetching patients by phone number ${phoneNumber}`, { error });
    throw new Error("Error fetching patients by phone number");
  }

  return data?.map(mapPatientRowToPatient) ?? [];
}, "getPatientsByPhoneNumber");

type GetCanonicalPatientParams = {
  groupId: string;
  patientId: string;
};

export const getCanonicalPatient = withDurationMetric(async function getCanonicalPatient(
  params: GetCanonicalPatientParams
): Promise<Patient | null> {
  const { groupId, patientId } = params;
  const { data, error } = await withRetry("getCanonicalPatient", async () => {
    const supabaseClient = await getSupabaseClient();

    return supabaseClient
      .from(PATIENT_TABLE_NAME)
      .select(dedupePatientSelectWithCanonical)
      .eq("id", patientId)
      .eq("group_id", groupId)
      .is("deleted_at", null);
  });

  if (error) throw error;

  if (!data || data.length === 0) return null;

  const patientRow = data[0];
  const patientDedupe = patientRow.patient_dedupe;
  const canonicalPatient = patientDedupe?.canonical_patient ?? patientRow;

  return mapPatientRowToPatient(canonicalPatient);
}, "getCanonicalPatient");

export const PATIENTS_METADATA_VIEW_NAME = "patients_metadata";
const PATIENT_METADATA_BATCH_SIZE = 250;

function mapPatientMetadataRowToPatientMetadata(patientMetadataRow: any): PatientMetadata {
  const mappedFields: Record<string, any> = {};

  for (const [key, value] of Object.entries(patientMetadataRow)) {
    mappedFields[customCamelCase(key)] = value;
  }

  // TODO: this cast isn't validated against patientMetadataSchema — a DB/model field-name drift
  // fails silently instead of erroring, should use patientMetadataSchema.safeParse
  return {
    ...mappedFields,
  } as PatientMetadata;
}

// Columns with real null values, where nulls should keep sorting to the end regardless of
// direction. Omitting nullsFirst for the rest lets Postgres use the column's index for
// ORDER BY + LIMIT.
const NULLABLE_PATIENT_SORT_COLUMNS = new Set(["date_of_birth"]);

function buildPatientOrderClause(sortBy: string, sortOrder: "asc" | "desc") {
  // Map sortBy to actual column names in patients_metadata view
  const sortColumnMap: Record<string, string> = {
    createdAt: "created_at",
    updatedAt: "updated_at",
    displayId: "display_id",
    patientName: "family_name",
    leadName: "family_name",
    dateOfBirth: "date_of_birth",
    status: "status",
    type: "type",
  };

  const column = sortColumnMap[sortBy] || sortBy;
  const ascending = sortOrder === "asc";

  return {
    column,
    options: {
      ascending,
      ...(NULLABLE_PATIENT_SORT_COLUMNS.has(column) ? { nullsFirst: false } : {}),
    },
  };
}

/**
 * Get paginated patients metadata using the patients_metadata view
 */
export const getPaginatedPatientsMetadata = withDurationMetric(async function getPaginatedPatientsMetadata(
  options: PaginatedPatientFiltersParams,
  group: Group
): Promise<{
  patients: Record<string, PatientMetadata>;
  totalCount: number;
  hasMore: boolean;
  page: number;
  pageSize: number;
}> {
  if (!group) {
    throw new Error("No group provided");
  }

  const { page = 0, pageSize = 30, status, type, sortBy = "createdAt", sortOrder = "desc" } = options;

  const supabaseClient = await getSupabaseClient();

  let query = supabaseClient.from(PATIENTS_METADATA_VIEW_NAME).select("*", { count: "exact" }).eq("group_id", group.id);

  // Apply search query if provided. A query that reads as a (partial) phone number is matched
  // against the patient's home/mobile phone columns; anything else is a tokenized name/display-id
  // search so "First Last" works even when search_text has a middle name or extra spaces.
  if (options.query) {
    const trimmedQuery = options.query.trim();

    if (isPartialPhoneQuery(trimmedQuery)) {
      const digits = normalizePhoneToNanpDigits(trimmedQuery) ?? trimmedQuery.replace(/\D/g, "");
      const hyphenated = formatPhoneNumberAsTyped(digits);

      query = query.or(
        `home_phone_number.ilike.%${hyphenated}%,mobile_phone_number.ilike.%${hyphenated}%,home_phone_number.ilike.%${digits}%,mobile_phone_number.ilike.%${digits}%`
      );
    } else {
      query = applySearchTextTokenFilter(query, trimmedQuery);
    }
  }

  // Apply filters
  if (status && status.length > 0) {
    query = query.in("status", status);
  }

  if (type && type.length > 0) {
    query = query.in("type", type);
  }

  const orderClause = buildPatientOrderClause(sortBy, sortOrder);

  query = query.order(orderClause.column, orderClause.options);

  const { data: patientRows, error, count } = await query.range(page * pageSize, (page + 1) * pageSize - 1);

  if (error) {
    logger.error("Error fetching paginated patients metadata:", {
      error,
      page,
      pageSize,
    });
    throw new Error("Error fetching paginated patients metadata");
  }

  if (!patientRows) {
    logger.error("No patients metadata found", {
      error,
      page,
      pageSize,
    });
    throw new Error("No patients metadata found");
  }

  // Convert array to map
  const patientsMap: Record<string, PatientMetadata> = {};

  patientRows.map(mapPatientMetadataRowToPatientMetadata).forEach((patientMetadata: PatientMetadata) => {
    if (patientMetadata.id) {
      patientsMap[patientMetadata.id] = patientMetadata;
    }
  });

  const totalCount = count ?? 0;
  const hasMore = totalCount > (page + 1) * pageSize;

  return {
    patients: patientsMap,
    totalCount,
    hasMore,
    page,
    pageSize,
  };
}, "getPaginatedPatientsMetadata");

export const getPatientsMetadataByIds = withDurationMetric(async function getPatientsMetadataByIds(
  ids: string[],
  group: Group
): Promise<PatientMetadata[]> {
  if (!ids || ids.length === 0) {
    return [];
  }

  const uniqueIds = [...new Set(ids)];
  const results: PatientMetadata[] = [];

  for (let i = 0; i < uniqueIds.length; i += PATIENT_METADATA_BATCH_SIZE) {
    const chunk = uniqueIds.slice(i, i + PATIENT_METADATA_BATCH_SIZE);

    const { data, error } = await withRetry("getPatientsMetadataByIds", async () => {
      const supabaseClient = await getSupabaseClient();

      return supabaseClient.from(PATIENTS_METADATA_VIEW_NAME).select("*").eq("group_id", group.id).in("id", chunk);
    });

    if (error) {
      logger.error("Error fetching patients metadata by ids", {
        error,
        totalIds: uniqueIds.length,
        chunkSize: chunk.length,
        chunkStartIndex: i,
      });
      throw new Error("Error fetching patients metadata");
    }

    results.push(...(data ?? []).map(mapPatientMetadataRowToPatientMetadata));
  }

  return results;
}, "getPatientsMetadataByIds");

export const getAllPatientIds = withDurationMetric(async function getAllPatientIds(
  group: Group,
  type?: PatientType
): Promise<Pick<Patient, "id" | "ehrId">[]> {
  const { data, error } = await withRetry("getAllPatientIds", async () => {
    const supabaseClient = await getSupabaseClient();

    let query = supabaseClient
      .from(PATIENT_TABLE_NAME)
      .select("id, ehr_id")
      .eq("group_id", group.id)
      .is("deleted_at", null)
      .not("is_duplicate", "is", true);

    if (type) {
      query = query.eq("type", type.toString());
    }

    return query;
  });

  if (error) {
    logger.error("Error fetching all patient ids", {
      error,
    });
    throw new Error("Error fetching all patient ids");
  }

  return data?.map((d) => ({ id: d.id, ehrId: d.ehr_id })) ?? [];
}, "getAllPatientIds");

export const getPatientIdMapsByEhrIdentifiers = withDurationMetric(async function getPatientIdMapsByEhrIdentifiers(
  group: Group
): Promise<{
  patientIdByEhrId: Map<string, string>;
  patientIdBySecondaryEhrId: Map<string, string>;
}> {
  const { data, error } = await withRetry("getPatientIdMapsByEhrIdentifiers", async () => {
    const supabaseClient = await getSupabaseClient();

    return supabaseClient
      .from(PATIENT_TABLE_NAME)
      .select("id, ehr_id, secondary_ehr_id")
      .eq("group_id", group.id)
      .is("deleted_at", null)
      .not("is_duplicate", "is", true);
  });

  if (error) {
    throw logAndThrow("Error fetching patient identifier maps", {
      error,
    });
  }

  const patientIdByEhrId = new Map<string, string>();
  const patientIdBySecondaryEhrId = new Map<string, string>();

  // Data might be returned as null or undefined, so we need to check for that.
  for (const patient of data ?? []) {
    if (patient.ehr_id) {
      patientIdByEhrId.set(patient.ehr_id, patient.id);
    }

    if (patient.secondary_ehr_id) {
      patientIdBySecondaryEhrId.set(patient.secondary_ehr_id, patient.id);
    }
  }

  return {
    patientIdByEhrId,
    patientIdBySecondaryEhrId,
  };
}, "getPatientIdMapsByEhrIdentifiers");
